import { z } from "zod";
import { AuditTrailEntryType } from "../AuditTrailEntryType.js";
// Section 12 payment-request audit `data` schemas. See `registry.ts` for the
// audit-data leniency contract these follow.
export const section12PaymentRequestCreatedDataSchema = z.object({
    doctorUserId: z.string(),
    formContextId: z.string(),
    amhpTeamId: z.string(),
    localAuthorityId: z.string().nullable(),
    amhpTeamName: z.string().nullish(),
    localAuthorityName: z.string().nullish(),
    localAuthorityOdsCode: z.string().nullish(),
    assessmentDateTime: z.string().nullish(),
    assessmentLocationAddress: z.string().nullish(),
    assessmentLocationPostcode: z.string().nullish(),
    isEstimated: z.boolean().nullish(),
});
export const section12PaymentRequestUpdatedDataSchema = z.object({
    doctorUserId: z.string(),
    previousStatus: z.string(),
    newStatus: z.string(),
    amhpTeamId: z.string(),
    amhpTeamName: z.string(),
    localAuthorityId: z.string().nullable(),
    assessmentDateTime: z.string().nullable(),
    assessmentLocationAddress: z.string().nullable(),
    assessmentLocationPostcode: z.string().nullable(),
    previousOutcomeType: z.string(),
    outcomeType: z.string(),
    previousFormId: z.string().nullable(),
    formId: z.string().nullable(),
    previousNoMedRecReason: z.string().nullable(),
    noMedRecReason: z.string().nullable(),
});
const section12PaymentRequestItemSnapshotSchema = z.object({
    itemType: z.string(),
    amountPence: z.number(),
    configuredRateKey: z.string().nullable(),
    mileageDistanceMiles: z.number().nullable(),
    receiptCount: z.number(),
});
export const section12PaymentRequestLimitedEditDataSchema = z.object({
    doctorUserId: z.string(),
    status: z.string(),
    scope: z.enum(["receipts-only", "value-and-receipts"]),
    previousItems: z.array(section12PaymentRequestItemSnapshotSchema),
    newItems: z.array(section12PaymentRequestItemSnapshotSchema),
});
export const section12PaymentRequestDeletedDataSchema = z.object({
    doctorUserId: z.string(),
    previousStatus: z.string(),
    amhpTeamId: z.string(),
    formContextId: z.string(),
    xeroInvoiceId: z.string().nullable(),
    xeroBillVoided: z.boolean().nullable(),
});
export const section12PaymentRequestStatusChangedDataSchema = z.object({
    before: z.object({ status: z.string() }),
    after: z.object({
        status: z.string(),
        rejectedReason: z.string().nullish(),
        amhpFeedback: z
            .object({
            notes: z.string().nullish(),
            rating: z.number().nullish(),
        })
            .nullish(),
    }),
    localAuthorityId: z.string().nullish(),
    localAuthorityName: z.string().nullish(),
    localAuthorityOdsCode: z.string().nullish(),
    assessmentDateTime: z.string().nullish(),
    assessmentLocationAddress: z.string().nullish(),
    assessmentLocationPostcode: z.string().nullish(),
    isEstimated: z.boolean().nullish(),
});
export const section12PaymentRequestFeesAdjustedDataSchema = z.object({
    reason: z.string(),
    adjustments: z.array(z.object({
        itemId: z.string(),
        itemLabel: z.string(),
        previousAmountPence: z.number(),
        newAmountPence: z.number(),
    })),
});
const section12LocalAuthoritySnapshotSchema = z.object({
    localAuthorityId: z.string().nullable(),
    localAuthorityOdsCode: z.string().nullable(),
    localAuthorityName: z.string().nullable(),
});
export const section12PaymentRequestLocalAuthorityChangedDataSchema = z.object({
    before: section12LocalAuthoritySnapshotSchema,
    after: section12LocalAuthoritySnapshotSchema,
    previousPaymentReference: z.string().nullish(),
    newPaymentReference: z.string().nullish(),
    reason: z.string(),
});
export const section12PaymentRequestCsvExportDataSchema = z.object({
    fromDate: z.string().nullish(),
    toDate: z.string().nullish(),
    status: z.string().nullish(),
    scope: z.enum(["doctor", "payment-reviewer", "finance"]).nullish(),
    teamId: z.string().nullish(),
    doctorUserId: z.string().nullish(),
    amhpTeamId: z.string().nullish(),
    localAuthorityId: z.string().nullish(),
});
export const section12PaymentRequestViewedDataSchema = z.object({
    paymentRequestId: z.string().nullish(),
    action: z.literal("viewChangeHistory").nullish(),
});
export const section12PaymentRequestsListedDataSchema = z.object({
    source: z.enum(["doctor", "payment-reviewer", "admin", "form-context"]),
    resultCount: z.number(),
});
export const section12ReceiptDownloadedDataSchema = z.object({
    paymentRequestId: z.string(),
    receiptId: z.string(),
});
export const section12PaymentRequestMovedDataSchema = z.object({
    formId: z.string(),
    supportingFormIds: z.array(z.string()),
    affectedFormContextIds: z.array(z.string()),
});
// ===========================================================================
// Section 12 doctor finder / availability / calendar.
//
// Update events carry opaque `before` / `after` snapshots (profile objects, arrays,
// scalars) that nothing reads — modelled `z.unknown()`.
// ===========================================================================
// Shared empty payload.
export const emptySection12DoctorFinderDataSchema = z.object({});
// before+after snapshot pair (opaque).
export const section12BeforeAfterDataSchema = z.object({
    before: z.unknown(),
    after: z.unknown(),
});
// after-only (create events).
export const section12AfterOnlyDataSchema = z.object({
    after: z.unknown(),
});
// before-only (delete events).
export const section12BeforeOnlyDataSchema = z.object({
    before: z.unknown(),
});
// Availability override update/delete carry the range they touched, plus opaque
// before/after snapshots. Audit rows are immutable historical snapshots: rows
// written against a week rather than a date range must keep parsing, so none of
// these date fields may ever be made required.
export const section12AvailabilityOverrideDataSchema = z.object({
    fromDate: z.string().nullish(),
    toDate: z.string().nullish(),
    weekStartDate: z.string().nullish(),
    before: z.unknown(),
    after: z.unknown(),
});
export const section12UpdateHomeBaseLocationDataSchema = z.object({
    useProfileAddress: z.boolean().nullish(),
    address: z.string().nullish(),
    postalCode: z.string().nullish(),
});
// ===== Calendar =====
export const section12CalendarConnectedDataSchema = z.object({
    providerType: z.string().nullish(),
    connectionId: z.string().nullish(),
});
export const section12CalendarDisconnectedDataSchema = z.object({
    connectionId: z.string().nullish(),
});
export const section12CalendarSyncToggledDataSchema = z.object({
    enabled: z.boolean().nullish(),
});
// Single-connection trigger ({ connectionId }) | all-calendars trigger ({ syncAll }).
export const section12CalendarSyncTriggeredDataSchema = z.object({
    connectionId: z.string().nullish(),
    syncAll: z.boolean().nullish(),
});
// ===== Search / approval =====
export const section12DoctorSearchDataSchema = z.object({
    localAuthorityId: z.string().nullish(),
    resultCount: z.number().nullish(),
});
export const section12ApprovalCheckedDataSchema = z.object({
    gmcNumber: z.string().nullish(), // staff PII
    result: z.string().nullish(), // "found" | "not-found" | "error" | "queued"
    section12ExpiryDate: z.string().nullish(),
    updated: z.boolean().nullish(),
    source: z.string().nullish(),
    jobId: z.string().nullish(),
});
// Audit type → schema entries for this file, composed in ../registry.ts.
export const section12AuditDataRegistry = {
    // Payments.
    [AuditTrailEntryType.section12PaymentRequestCreated]: section12PaymentRequestCreatedDataSchema,
    [AuditTrailEntryType.section12PaymentRequestUpdated]: section12PaymentRequestUpdatedDataSchema,
    [AuditTrailEntryType.section12PaymentRequestLimitedEdit]: section12PaymentRequestLimitedEditDataSchema,
    [AuditTrailEntryType.section12PaymentRequestDeleted]: section12PaymentRequestDeletedDataSchema,
    [AuditTrailEntryType.section12PaymentRequestStatusChanged]: section12PaymentRequestStatusChangedDataSchema,
    [AuditTrailEntryType.section12PaymentRequestFeesAdjusted]: section12PaymentRequestFeesAdjustedDataSchema,
    [AuditTrailEntryType.section12PaymentRequestLocalAuthorityChanged]: section12PaymentRequestLocalAuthorityChangedDataSchema,
    [AuditTrailEntryType.section12PaymentRequestCsvExport]: section12PaymentRequestCsvExportDataSchema,
    [AuditTrailEntryType.section12PaymentRequestViewed]: section12PaymentRequestViewedDataSchema,
    [AuditTrailEntryType.section12PaymentRequestsListed]: section12PaymentRequestsListedDataSchema,
    [AuditTrailEntryType.section12ReceiptDownloaded]: section12ReceiptDownloadedDataSchema,
    [AuditTrailEntryType.section12PaymentRequestMoved]: section12PaymentRequestMovedDataSchema,
    // Doctor finder — profile attributes.
    [AuditTrailEntryType.section12SpecialismCreated]: section12AfterOnlyDataSchema,
    [AuditTrailEntryType.section12SpecialismUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12SpecialismDeleted]: section12BeforeOnlyDataSchema,
    [AuditTrailEntryType.section12LanguageCreated]: section12AfterOnlyDataSchema,
    [AuditTrailEntryType.section12LanguageUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12GenderUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12TrustAffiliationUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12UpdateHomeBaseLocation]: section12UpdateHomeBaseLocationDataSchema,
    [AuditTrailEntryType.section12MaxTravelTimeUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12ContactPreferencesUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12UserLanguagesUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12UserSpecialismsUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12UserLocalAuthorityMappingsUpdated]: section12BeforeAfterDataSchema,
    // Availability.
    [AuditTrailEntryType.section12UserAvailabilityUpdated]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12AvailabilityOverrideUpdated]: section12AvailabilityOverrideDataSchema,
    [AuditTrailEntryType.section12AvailabilityOverrideDeleted]: section12AvailabilityOverrideDataSchema,
    // Calendar.
    [AuditTrailEntryType.section12CalendarConnectionsListed]: emptySection12DoctorFinderDataSchema,
    [AuditTrailEntryType.section12CalendarConnected]: section12CalendarConnectedDataSchema,
    [AuditTrailEntryType.section12CalendarDisconnected]: section12CalendarDisconnectedDataSchema,
    [AuditTrailEntryType.section12CalendarSyncToggled]: section12CalendarSyncToggledDataSchema,
    [AuditTrailEntryType.section12CalendarSyncTriggered]: section12CalendarSyncTriggeredDataSchema,
    // Phone / search / approval.
    [AuditTrailEntryType.section12PhoneVerified]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12PhoneRemoved]: section12BeforeAfterDataSchema,
    [AuditTrailEntryType.section12PhoneVerificationCodeSent]: emptySection12DoctorFinderDataSchema,
    [AuditTrailEntryType.section12DoctorSearch]: section12DoctorSearchDataSchema,
    [AuditTrailEntryType.section12ApprovalChecked]: section12ApprovalCheckedDataSchema,
};
//# sourceMappingURL=section12.js.map